import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  AfterViewInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoFocusModule } from 'primeng/autofocus';

@Component({
  selector: 'app-opt-input',
  standalone: true,
  imports: [CommonModule, FormsModule, AutoFocusModule],
  templateUrl: './opt-input.component.html',
  styleUrls: ['./opt-input.component.scss'],
})
export class OptInputComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  otp: string[] = ['', '', '', '', '', ''];
  countdownInterval: ReturnType<typeof setInterval> | undefined;
  localTimer: number = 0;
  @Input() otpResponseStatus: 1 | 2 | null = null;
  @Input() otpTimer: number = 180;
  @Input() loading = false;
  @Output() otpReady = new EventEmitter<string>();
  @Output() requestOtpCode = new EventEmitter<any>();
  @Output() isOtpValid = new EventEmitter<boolean>();
  @Output() resetOtpResponseStatus = new EventEmitter<void>();
  validOtp: boolean = false;

  @ViewChildren('otpInput') otpInputElements!: QueryList<ElementRef>;
  private inputListeners = new Map<
    ElementRef,
    {
      input?: (e: Event) => void;
      paste?: (e: ClipboardEvent) => void;
      keydown?: (e: KeyboardEvent) => void;
    }
  >();

  ngOnInit(): void {
    this.localTimer = this.otpTimer;
    this.startCountdown();
  }

  ngAfterViewInit(): void {
    this.attachInputListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['otpResponseStatus']?.currentValue === 2) {
      this.cleanInput();
    }
  }

  private attachInputListeners(): void {
    this.otpInputElements.forEach((element, index) => {
      const inputElement = element.nativeElement;

      const handleInputFn = () => this.handleInput(inputElement, index);
      const handlePasteFn = (event: ClipboardEvent) =>
        this.handlePaste(event, index);
      const handleKeyDownFn = (event: KeyboardEvent) =>
        this.handleKeyDown(event, index);

      inputElement.addEventListener('input', handleInputFn);
      inputElement.addEventListener('paste', handlePasteFn);
      inputElement.addEventListener('keydown', handleKeyDownFn);

      this.inputListeners.set(element, {
        input: handleInputFn,
        paste: handlePasteFn,
        keydown: handleKeyDownFn,
      });
    });
  }

  handleInput(inputElement: HTMLInputElement, index: number): void {
    let inputValue = inputElement.value;
    inputValue = inputValue.replace(/[^0-9]/g, '').substring(0, 1);
    this.otp[index] = inputValue;
    inputElement.value = inputValue;

    if (inputValue && index < this.otp.length - 1) {
      const nextInput = this.otpInputElements.get(index + 1)?.nativeElement;
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
    this.checkOtpValidity();
  }

  handleKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && index > 0 && !this.otp[index]) {
      const prevInput = this.otpInputElements.get(index - 1)?.nativeElement;
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      this.handlePasteFromClipboard(index);
      event.preventDefault();
    }
    this.preventInvalidInput(event);
  }

  preventInvalidInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent, index: number) {
    let pastedData = event.clipboardData?.getData('text') || '';
    pastedData = pastedData.replace(/[^0-9]/g, '');
    if (pastedData) {
      for (
        let i = 0;
        i < pastedData.length && index + i < this.otp.length;
        i++
      ) {
        this.otp[index + i] = pastedData[i];
      }
      this.otpInputElements.forEach((element, i) => {
        element.nativeElement.value = this.otp[i] || '';
      });
      setTimeout(() => {
        const nextEmptyIndex = this.otp.findIndex((val) => !val);
        const focusIndex =
          nextEmptyIndex === -1 ? this.otp.length - 1 : nextEmptyIndex;

        if (focusIndex < this.otpInputElements.length) {
          const inputToFocus = this.otpInputElements.get(focusIndex);
          if (inputToFocus) {
            inputToFocus.nativeElement.focus();
            inputToFocus.nativeElement.select();
          }
        }
      }, 0);

      this.checkOtpValidity();
      event.preventDefault();
    }
  }

  private async handlePasteFromClipboard(index: number) {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const pastedData = clipboardText.replace(/[^0-9]/g, '');

      if (pastedData) {
        for (
          let i = 0;
          i < pastedData.length && index + i < this.otp.length;
          i++
        ) {
          this.otp[index + i] = pastedData[i];
        }
        this.otpInputElements.forEach((element, i) => {
          element.nativeElement.value = this.otp[i] || '';
        });

        setTimeout(() => {
          const nextEmptyIndex = this.otp.findIndex((val) => !val);
          const focusIndex =
            nextEmptyIndex === -1 ? this.otp.length - 1 : nextEmptyIndex;

          if (focusIndex < this.otpInputElements.length) {
            const inputToFocus = this.otpInputElements.get(focusIndex);
            if (inputToFocus) {
              inputToFocus.nativeElement.focus();
              inputToFocus.nativeElement.select();
            }
          }
        }, 0);
        this.checkOtpValidity();
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  }

  startCountdown(): void {
    if (this.countdownInterval !== undefined) {
      clearInterval(this.countdownInterval);
    }
    this.countdownInterval = setInterval(() => {
      this.localTimer--;
      this.cdr.markForCheck();
      if (this.localTimer <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = undefined;
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  resendCode(): void {
    this.cleanInput();
    this.localTimer = this.otpTimer;
    this.otpResponseStatus = null;
    this.startCountdown();
    this.requestOtpCode.emit();
    this.checkOtpValidity();
  }

  cleanInput() {
    this.otp = ['', '', '', '', '', ''];
  }

  checkOtpValidity() {
    this.validOtp = this.otp.every((val) => val.length > 0);
    if (this.validOtp) {
      this.otpReady.emit(this.otp.join(''));
      this.resetOtpResponseStatus.emit();
    }
    this.isOtpValid.emit(this.validOtp);
  }

  ngOnDestroy(): void {
    if (this.countdownInterval !== undefined) {
      clearInterval(this.countdownInterval);
    }
    this.inputListeners.forEach((listeners, element) => {
      const inputElement = element.nativeElement;
      if (listeners.input) {
        inputElement.removeEventListener('input', listeners.input);
      }
      if (listeners.paste) {
        inputElement.removeEventListener('paste', listeners.paste);
      }
      if (listeners.keydown) {
        inputElement.removeEventListener('keydown', listeners.keydown);
      }
    });
    this.inputListeners.clear();
  }
}
